
// @flow
import moment from 'moment-timezone';
import { map, values } from 'ramda';

import type { OrderRequest, Params } from 'app/common/components/Cafeteria/dataTypes/orderRequests/OrderRequest.types';
import { statuses, type Status } from 'app/common/components/Cafeteria/dataTypes/orderRequests/statuses';
import { roles, type Role } from 'app/common/components/Cafeteria/dataTypes/orderRequests/roles';
import { foodModes, type FoodMode } from 'app/common/components/Cafeteria/dataTypes/orderRequests/foodModes';
import { groupTypes, type GroupType } from 'app/common/components/Cafeteria/dataTypes/orderRequests/groupTypes';
import SimpleDate from 'app/common/classes/simple-date';
import Person from 'app/common/classes/person';

import type { Request } from '../../../index';


const dateFormatApi = moment.ISO_8601;
const dateFormatParamsApi = 'YYYY-MM-DD';
const browserTimezone = { timezone: SimpleDate.browserTimezone() };

type StatusApi =
  | 0 // Ожидает выдачи
  | 1 // Ожидает проведения
  | 2 // Проведена
  | 3 // Открытая заявка
  | 4 // Ожидает обработки
  | -1 // Удалена

const statusMap = (s: StatusApi): Status | null => {
  switch (s) {
    case 0:
      return statuses.WaitingForIssuance;
    case 1:
      return statuses.WaitingForSpend;
    case 2:
      return statuses.Spent;
    case 3:
      return statuses.OpenedRequest;
    case 4:
      return statuses.WaitingForProcessing;
    case -1:
      return statuses.Removed;
    default:
      return null;
  }
};

const rolesMap = (s): Role | null => {
  switch (s) {
    case 'cafeteria':
      return roles.Cafeteria;
    case 'dealer':
      return roles.Dealer;
    case 'school':
      return roles.School;
    case 'admin':
      return roles.Admin;
    case 'template':
      return roles.Template;
    default:
      return null;
  }
};

const foodModesMap = (s): FoodMode | null => {
  switch (s) {
    case 'breakfast':
      return foodModes.Breakfast;
    case 'dinner':
      return foodModes.Dinner;
    case 'afternoon':
      return foodModes.Afternoon;
    default:
      return null;
  }
};

const groupTypesMap = (s): GroupType | null => {
  switch (s) {
    case 'Klass':
      return groupTypes.Klass;
    case 'DayCare':
      return groupTypes.DayCare;
    default:
      return null;
  }
};


type ParamsApi = {
  from_date: string,
  to_date: string,
  customer_id: number,
  status?: StatusApi,
}

type OrderCommonApi = {
  id: number,
  status: StatusApi,
  date: ?string,
  change_id: number, // метка изменений, должна быть прислана при изменении заявки
  created_user_role: ?(
    | 'cafeteria' // сотрудник столовой
    | 'dealer' // сотрудник оператора
    | 'school' // сотрудник ОУ
    | 'admin' // администратор системы
    | 'template' // создано по шаблону
  ),
  created_user_name: ?{
    first: string,
    last: string,
    middle: string,
  },
  group_type: ?(
    | 'Klass'
    | 'DayCare'
  ),
  group_title: ?string,
  food_mode: ?(
    | 'breakfast'
    | 'dinner'
    | 'afternoon'
  ),
  total_sum: ?number,
}

type OrderApi = { // заявка с заменой
  ...OrderCommonApi,
  time_restriction: 'expired', // время редактирования заявки истекло
  edit_mode: 'replace' // при редактировании возможна только замена
} | { // заявка со свободным выбором
  ...OrderCommonApi,
  time_restriction: 'available', // время редактирования заявки не истекло
  expires_at: string, // возможно редактировать до...
  edit_mode: 'free' // свободный выбор комплектов
} | { // открытая заявка
  ...OrderCommonApi,
  time_restriction: null,
  expires_at: string
}

type OrdersApi = { [string]: OrderApi }

export const convertParams = (params: Params): [ParamsApi, null] => {
  const { date, monthly, customerId, campId } = params;
  const periodUnit = monthly ? SimpleDate.units.month : SimpleDate.units.day;
  const [from, to] = date.getPeriod(periodUnit, dateFormatParamsApi);
  return [{
    from_date: from,
    to_date: to,
    customer_id: parseInt(customerId, 10),
    for_camp: !!campId,
    school_id: campId,
  }, null];
};

export const convertResult = (orders: OrdersApi): Array<OrderRequest> => {
  return map(({
    id,
    status,
    date,
    created_user_name: name,
    created_user_role: role,
    group_title: groupTitle,
    group_type: groupType,
    food_mode: foodMode,
    total_sum: total,
    change_id: changeId,
    time_restriction: timeRestriction,
    expires_at: expiresAt = null,
    edit_mode: editMode = null,
  }) => ({
    id: String(id),
    status: statusMap(status),
    date: date ? new SimpleDate(date, dateFormatApi, browserTimezone) : null,
    createdBy: name ? new Person({ ...name }) : null,
    createdByRole: rolesMap(role),
    groupTitle: groupTitle || '',
    groupType: groupTypesMap(groupType),
    foodMode: foodModesMap(foodMode),
    total: typeof total === 'number' ? total : null,
    changeId: String(changeId),
    display: {
      putOnFoodOption: timeRestriction !== 'expired' && status === 3,
      removeFromFoodOption: timeRestriction !== 'expired' && status === 0 && !!editMode,
      changeOption: timeRestriction !== 'expired' && status === 0 && editMode === 'free',
      exchangeComplexOption: timeRestriction !== 'expired' && status === 0 && editMode === 'replace',
      expiredMessage: timeRestriction === 'expired',
      expiredUntillMessage: timeRestriction === 'available',
    },
    expiresAt: expiresAt ? new SimpleDate(expiresAt, dateFormatApi, browserTimezone) : null,
    putOnFoodOnlySingleComplex: editMode === 'replace',
  }), values(orders));
};

export const GET_CAFETERIA_CUSTOMER_ORDERS: Request<
  Params,
  Array<OrderRequest>,
  void
> = [
  'cafeteria/customer/orders',
  'get',
  ['data', 'orders'],
];
